import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import config from "../../config";
import "./style.scss";

const backendUrl = config.backend_url;

const getAuthHeader = () => {
  const raw = localStorage.getItem("authToken") || "";
  if (!raw) return {};
  return { Authorization: raw.startsWith("Bearer ") ? raw : `Bearer ${raw}` };
};

const EXCLUDED_POSITIONS = ["mdd", "dealer", "spd", "smd"];

const prettifyLabel = (key = "") => {
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const displayValue = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    value === "NA" ||
    (typeof value === "number" && Number.isNaN(value))
  ) {
    return "NA";
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
};

const getStatusClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "active";
  if (s === "inactive") return "inactive";
  return "neutral";
};

const parsePolicyFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "n", "off"].includes(normalized)) return false;
  }
  return null;
};

const buildMetadataForm = (metadata = {}, code = "", name = "") => {
  const cleaned = metadata && typeof metadata === "object" ? metadata : {};

  return {
    name: cleaned.name && cleaned.name !== "NA" ? cleaned.name : name || "",
    code: cleaned.code && cleaned.code !== "NA" ? cleaned.code : code || "",
    system_code:
      cleaned.system_code && cleaned.system_code !== "NA"
        ? cleaned.system_code
        : code || "",
    firm_code:
      cleaned.firm_code && cleaned.firm_code !== "NA" ? cleaned.firm_code : "",
    attendance:
      typeof cleaned.attendance === "boolean"
        ? cleaned.attendance
        : cleaned.attendance === "Yes"
        ? true
        : cleaned.attendance === "No"
        ? false
        : false,
    leaves:
      typeof cleaned.leaves === "boolean"
        ? cleaned.leaves
        : cleaned.leaves === "Yes"
        ? true
        : cleaned.leaves === "No"
        ? false
        : false,
    use_payroll_policy:
      typeof cleaned.use_payroll_policy === "boolean"
        ? cleaned.use_payroll_policy
        : cleaned.use_payroll_policy === "Yes"
        ? true
        : cleaned.use_payroll_policy === "No"
        ? false
        : false,
    mandatory_market_coverage:
      typeof cleaned.mandatory_market_coverage === "boolean"
        ? cleaned.mandatory_market_coverage
        : cleaned.mandatory_market_coverage === "Yes"
        ? true
        : cleaned.mandatory_market_coverage === "No"
        ? false
        : false,
    allow_attendance_from_dealer_point:
      typeof cleaned.allow_attendance_from_dealer_point === "boolean"
        ? cleaned.allow_attendance_from_dealer_point
        : cleaned.allow_attendance_from_dealer_point === "Yes"
        ? true
        : cleaned.allow_attendance_from_dealer_point === "No"
        ? false
        : false,
    basic_salary:
      cleaned.basic_salary && cleaned.basic_salary !== "NA"
        ? cleaned.basic_salary
        : "",
    allowed_leaves:
      cleaned.allowed_leaves && cleaned.allowed_leaves !== "NA"
        ? cleaned.allowed_leaves
        : "",
    leaves_balance:
      cleaned.leaves_balance && cleaned.leaves_balance !== "NA"
        ? cleaned.leaves_balance
        : "",
    punch_location:
      cleaned.punch_location && cleaned.punch_location !== "NA"
        ? cleaned.punch_location
        : "",
  };
};

export default function UserDirectoryPage() {
  const [loading, setLoading] = useState(false);
  const [updatingCode, setUpdatingCode] = useState("");
  const [rows, setRows] = useState([]);
  const [firmOptions, setFirmOptions] = useState([]);
  const [allMetadataKeys, setAllMetadataKeys] = useState([]);

  const [search, setSearch] = useState("");
  const [position, setPosition] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [firmCode, setFirmCode] = useState("");

  const [positionOptions, setPositionOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  const [expandedCodes, setExpandedCodes] = useState({});

  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [bulkMetadataSaving, setBulkMetadataSaving] = useState(false);
  const [bulkMetadataField, setBulkMetadataField] = useState(
    "mandatory_market_coverage"
  );
  const [bulkMetadataValue, setBulkMetadataValue] = useState("true");
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [metaForm, setMetaForm] = useState(buildMetadataForm({}, "", ""));

  const [pageInfo, setPageInfo] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 1,
  });

  const fetchFirms = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/super-admin/user-directory/firms`,
        {
          headers: getAuthHeader(),
        }
      );

      setFirmOptions(res.data?.firms || []);
    } catch (error) {
      console.error("Failed to fetch firms:", error);
      setFirmOptions([]);
    }
  };

  const fetchData = async (customPage = 1) => {
    try {
      setLoading(true);

      const res = await axios.get(`${backendUrl}/super-admin/user-directory`, {
        params: {
          search,
          position,
          role,
          status,
          firm_code: firmCode,
          page: customPage,
          limit: pageInfo.limit,
        },
        headers: getAuthHeader(),
      });

      const apiRows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      const keys = Array.isArray(res.data?.allMetadataKeys)
        ? res.data.allMetadataKeys
        : [];

      setRows(apiRows);
      setAllMetadataKeys(keys);

      setPageInfo((prev) => ({
        ...prev,
        page: res.data?.page || customPage || 1,
        limit: res.data?.limit || prev.limit,
        total: res.data?.total || apiRows.length || 0,
        totalPages: res.data?.totalPages || 1,
      }));

      const posSet = new Set();
      const roleSet = new Set();

      apiRows.forEach((item) => {
        const pos = String(item?.position || "").toLowerCase();
        const rl = String(item?.role || "").toLowerCase();

        if (pos && pos !== "na" && !EXCLUDED_POSITIONS.includes(pos)) {
          posSet.add(pos);
        }

        if (rl && rl !== "na") {
          roleSet.add(rl);
        }
      });

      setPositionOptions(Array.from(posSet).sort());
      setRoleOptions(Array.from(roleSet).sort());
    } catch (error) {
      console.error("Failed to fetch user directory:", error);
      setRows([]);
      setAllMetadataKeys([]);
      setPageInfo((prev) => ({
        ...prev,
        total: 0,
        totalPages: 1,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFirms();
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const available = new Set(rows.map((row) => row?.code).filter(Boolean));
    setSelectedCodes((prev) => prev.filter((code) => available.has(code)));
  }, [rows]);

  const visibleMetadataKeys = useMemo(() => {
    const preferredOrder = [
      "attendance",
      "mandatory_market_coverage",
      "allow_attendance_from_dealer_point",
      "firm_code",
      "basic_salary",
      "allowed_leaves",
      "leaves",
      "use_payroll_policy",
      "leaves_balance",
      "system_code",
      "punch_location",
      "name",
      "code",
    ];

    const filtered = allMetadataKeys.filter(
      (key) => !["_id", "__v", "createdAt", "updatedAt"].includes(key)
    );

    const preferred = preferredOrder.filter((key) => filtered.includes(key));
    const rest = filtered.filter((key) => !preferredOrder.includes(key)).sort();

    return [...preferred, ...rest];
  }, [allMetadataKeys]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const pos = String(row?.position || "").toLowerCase();
      return !EXCLUDED_POSITIONS.includes(pos);
    });
  }, [rows]);

  const visibleCodes = useMemo(() => {
    return filteredRows.map((row) => row.code).filter(Boolean);
  }, [filteredRows]);

  const selectedVisibleCount = useMemo(() => {
    const visibleSet = new Set(visibleCodes);
    return selectedCodes.filter((code) => visibleSet.has(code)).length;
  }, [selectedCodes, visibleCodes]);

  const allVisibleSelected =
    visibleCodes.length > 0 && selectedVisibleCount === visibleCodes.length;

  const someVisibleSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visibleCodes.length;

  const toggleExpand = (code) => {
    setExpandedCodes((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  const handleFirmChange = async (code, firm_code) => {
    try {
      setUpdatingCode(code);

      await axios.patch(
        `${backendUrl}/super-admin/user-directory/${code}/firm`,
        { firm_code },
        { headers: getAuthHeader() }
      );

      await fetchData(pageInfo.page || 1);
    } catch (error) {
      console.error("Failed to update firm:", error);
      alert(error?.response?.data?.message || "Failed to update firm");
    } finally {
      setUpdatingCode("");
    }
  };

  const handleStatusToggle = async (row) => {
    const nextStatus =
      String(row?.is_active || "").toLowerCase() === "active"
        ? "inactive"
        : "active";

    try {
      setUpdatingCode(row.code);

      await axios.patch(
        `${backendUrl}/super-admin/user-directory/${row.code}/status`,
        { status: nextStatus },
        { headers: getAuthHeader() }
      );

      await fetchData(pageInfo.page || 1);
    } catch (error) {
      console.error("Failed to update status:", error);
      alert(error?.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingCode("");
    }
  };

  const openMetaModal = async (row) => {
    try {
      setSelectedUser(row);
      setMetaModalOpen(true);
      setMetaLoading(true);

      const res = await axios.get(
        `${backendUrl}/super-admin/user-directory/${row.code}/metadata`,
        {
          headers: getAuthHeader(),
        }
      );

      const metadata = res.data?.data?.metadata || row.metadata || null;
      const resolvedName = res.data?.data?.name || row.name || "";

      setMetaForm(buildMetadataForm(metadata, row.code, resolvedName));
    } catch (error) {
      console.error("Failed to fetch metadata:", error);
      setMetaForm(buildMetadataForm(row.metadata || {}, row.code, row.name));
    } finally {
      setMetaLoading(false);
    }
  };

  const closeMetaModal = () => {
    if (metaSaving) return;
    setMetaModalOpen(false);
    setSelectedUser(null);
    setMetaForm(buildMetadataForm({}, "", ""));
    setMetaLoading(false);
    setMetaSaving(false);
  };

  const handleMetaInput = (field, value) => {
    setMetaForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveMetadata = async () => {
    if (!selectedUser?.code) return;

    try {
      setMetaSaving(true);

      const payload = {
        ...metaForm,
        attendance: !!metaForm.attendance,
        leaves: !!metaForm.leaves,
        use_payroll_policy: !!metaForm.use_payroll_policy,
        mandatory_market_coverage: !!metaForm.mandatory_market_coverage,
        allow_attendance_from_dealer_point:
          !!metaForm.allow_attendance_from_dealer_point,
      };

      await axios.put(
        `${backendUrl}/super-admin/user-directory/${selectedUser.code}/metadata`,
        payload,
        { headers: getAuthHeader() }
      );

      await fetchData(pageInfo.page || 1);
      closeMetaModal();
    } catch (error) {
      console.error("Failed to save metadata:", error);
      alert(error?.response?.data?.message || "Failed to save metadata");
    } finally {
      setMetaSaving(false);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setPosition("");
    setRole("");
    setStatus("");
    setFirmCode("");
    setSelectedCodes([]);

    setTimeout(() => {
      setPageInfo((prev) => ({ ...prev, page: 1 }));
      fetchData(1);
    }, 0);
  };

  const applyBulkMetadataUpdate = async () => {
    if (loading || bulkMetadataSaving) return;

    const selectedTargetCodes = selectedCodes.filter(Boolean);
    const useSelected = selectedTargetCodes.length > 0;
    const targetCount = useSelected
      ? selectedTargetCodes.length
      : Number(pageInfo.total || 0);

    if (!targetCount) {
      alert("No users found for current filters.");
      return;
    }

    const fieldLabel =
      {
        mandatory_market_coverage: "Mandatory Market Coverage",
        allow_attendance_from_dealer_point: "Allow Attendance From Dealer Point",
        use_payroll_policy: "Payroll Policy",
        attendance: "Attendance",
        leaves: "Leaves",
      }[bulkMetadataField] || bulkMetadataField;

    const nextValue = bulkMetadataValue === "true";
    const confirmationMessage = `Set ${fieldLabel} ${
      nextValue ? "ON" : "OFF"
    } for ${targetCount} ${useSelected ? "selected" : "filtered"} user(s)?`;

    if (!window.confirm(confirmationMessage)) return;

    try {
      setBulkMetadataSaving(true);

      await axios.patch(
        `${backendUrl}/super-admin/user-directory/metadata/bulk`,
        {
          field: bulkMetadataField,
          value: nextValue,
          ...(useSelected
            ? { codes: selectedTargetCodes }
            : {
                search,
                position,
                role,
                status,
                firm_code: firmCode,
              }),
        },
        { headers: getAuthHeader() }
      );

      await fetchData(1);
      if (useSelected) setSelectedCodes([]);
      alert(
        `Bulk metadata updated successfully for ${
          useSelected ? "selected" : "filtered"
        } users: ${
          fieldLabel
        } ${nextValue ? "ON" : "OFF"}.`
      );
    } catch (error) {
      console.error("Failed to bulk update metadata:", error);
      alert(
        error?.response?.data?.message || "Failed to bulk update metadata"
      );
    } finally {
      setBulkMetadataSaving(false);
    }
  };

  const toggleSelectCode = (code) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code]
    );
  };

  const toggleSelectAllVisible = () => {
    setSelectedCodes((prev) => {
      if (allVisibleSelected || someVisibleSelected) {
        return prev.filter((code) => !visibleCodes.includes(code));
      }
      const merged = new Set([...prev, ...visibleCodes]);
      return Array.from(merged);
    });
  };

  return (
    <div className="user-directory-page">
      <div className="ud-hero">
        <div>
          <div className="ud-title">User Directory</div>
          <div className="ud-subtitle">
            Super admin directory for users, actor codes, firm mapping, and
            metadata.
          </div>
        </div>

        <div className="ud-hero-stats">
          <div className="stat-card">
            <span className="stat-label">Visible Users</span>
            <strong>{pageInfo.total || filteredRows.length}</strong>
          </div>
          <div className="stat-card">
            <span className="stat-label">Metadata Columns</span>
            <strong>{visibleMetadataKeys.length}</strong>
          </div>
        </div>
      </div>

      <div className="ud-filters-card">
        <div className="ud-filters-top">
          <div className="section-title">Filters</div>
          <div className="filter-actions">
            <button
              className="secondary-btn"
              onClick={resetFilters}
              disabled={loading}
            >
              Reset
            </button>
            <button
              className="primary-btn"
              onClick={() => fetchData(1)}
              disabled={loading}
            >
              {loading ? "Loading..." : "Apply Filters"}
            </button>
          </div>
        </div>

        <div className="ud-filters-grid">
          <div className="field">
            <label>Search</label>
            <input
              placeholder="Code / name / email / phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="field">
            <label>Position</label>
            <select
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            >
              <option value="">All Positions</option>
              {positionOptions.map((item) => (
                <option key={item} value={item}>
                  {item.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">All Roles</option>
              {roleOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="field">
            <label>Firm</label>
            <select
              value={firmCode}
              onChange={(e) => setFirmCode(e.target.value)}
            >
              <option value="">All Firms</option>
              {firmOptions.map((firm) => (
                <option key={firm.code} value={firm.code}>
                  {firm.name} ({firm.code})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bulk-policy-row">
          <div className="bulk-policy-text">Bulk Metadata Edit</div>
          <div className="bulk-policy-controls">
            <span className="bulk-scope-note">
              {selectedCodes.length > 0
                ? `Scope: ${selectedCodes.length} selected user(s)`
                : `Scope: all filtered users (${pageInfo.total || 0})`}
            </span>
            {selectedCodes.length > 0 && (
              <button
                className="secondary-btn small"
                onClick={() => setSelectedCodes([])}
                disabled={loading || bulkMetadataSaving}
              >
                Clear Selection
              </button>
            )}
            <select
              value={bulkMetadataField}
              onChange={(e) => setBulkMetadataField(e.target.value)}
              disabled={loading || bulkMetadataSaving}
            >
              <option value="mandatory_market_coverage">
                Mandatory Market Coverage
              </option>
              <option value="allow_attendance_from_dealer_point">
                Allow Attendance From Dealer Point
              </option>
              <option value="use_payroll_policy">Payroll Policy</option>
              <option value="attendance">Attendance</option>
              <option value="leaves">Leaves</option>
            </select>
            <select
              value={bulkMetadataValue}
              onChange={(e) => setBulkMetadataValue(e.target.value)}
              disabled={loading || bulkMetadataSaving}
            >
              <option value="true">Set ON</option>
              <option value="false">Set OFF</option>
            </select>
            <button
              className="primary-btn"
              onClick={applyBulkMetadataUpdate}
              disabled={loading || bulkMetadataSaving}
            >
              {bulkMetadataSaving ? "Applying..." : "Apply Bulk Edit"}
            </button>
          </div>
        </div>
      </div>

      <div className="ud-table-card">
        <div className="ud-table-head">
          <div className="section-title">Directory Table</div>
          <div className="table-note">
            Users without metadata show <strong>NA</strong>.
          </div>
        </div>

        {loading ? (
          <div className="ud-empty">Loading user directory...</div>
        ) : filteredRows.length === 0 ? (
          <div className="ud-empty">No users found.</div>
        ) : (
          <div className="ud-table-wrap">
            <table className="ud-table">
              <thead>
                <tr>
                  <th className="select-col">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = someVisibleSelected;
                      }}
                      onChange={toggleSelectAllVisible}
                      disabled={loading || !visibleCodes.length}
                      title="Select all visible users"
                    />
                  </th>
                  <th>User</th>
                  <th>Position</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Firm</th>
                  <th>Metadata</th>
                  {visibleMetadataKeys.map((key) => (
                    <th key={key}>{prettifyLabel(key)}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row) => {
                  const expanded = !!expandedCodes[row.code];

                  return (
                    <React.Fragment key={row.code}>
                      <tr>
                        <td className="select-col">
                          <input
                            type="checkbox"
                            checked={selectedCodes.includes(row.code)}
                            onChange={() => toggleSelectCode(row.code)}
                            disabled={loading}
                            title={`Select ${displayValue(row.name)}`}
                          />
                        </td>
                        <td>
                          <div className="user-cell">
                            <div className="user-name">
                              {displayValue(row.name)}
                            </div>
                            <div className="user-code">
                              {displayValue(row.code)}
                            </div>
                            <div className="user-mini">
                              Email: {displayValue(row.email)}
                            </div>
                            <div className="user-mini">
                              Phone: {displayValue(row.phone)}
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="pill neutral">
                            {displayValue(row.position).toUpperCase()}
                          </span>
                        </td>

                        <td>{displayValue(row.role)}</td>

                        <td>
                          <div className="status-stack">
                            <span
                              className={`pill ${getStatusClass(row.is_active)}`}
                            >
                              {displayValue(row.is_active)}
                            </span>
                            <div className="status-mini">
                              User: {displayValue(row.user_status)}
                            </div>
                            <div className="status-mini">
                              Actor: {displayValue(row.actor_status)}
                            </div>
                          </div>
                        </td>

                        <td>
                          <select
                            className="firm-select"
                            value={row.metadata?.firm_code || row.firm_code || ""}
                            onChange={(e) =>
                              handleFirmChange(row.code, e.target.value)
                            }
                            disabled={updatingCode === row.code}
                          >
                            <option value="">Select Firm</option>
                            {firmOptions.map((firm) => (
                              <option key={firm.code} value={firm.code}>
                                {firm.name} ({firm.code})
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <div className="metadata-stack">
                            <span
                              className={`pill ${
                                row.metadata_available ? "active" : "neutral"
                              }`}
                            >
                              {row.metadata_available ? "Available" : "Missing"}
                            </span>
                            <span
                              className={`pill policy ${
                                parsePolicyFlag(row.metadata?.use_payroll_policy) === true
                                  ? "active"
                                  : parsePolicyFlag(row.metadata?.use_payroll_policy) === false ||
                                    parsePolicyFlag(row.metadata?.use_payroll_policy) === null
                                  ? "inactive"
                                  : "neutral"
                              }`}
                            >
                              {parsePolicyFlag(row.metadata?.use_payroll_policy) === true
                                ? "PF/ESI On"
                                : "PF/ESI Off"}
                            </span>
                            <span
                              className={`pill policy ${
                                parsePolicyFlag(
                                  row.metadata?.mandatory_market_coverage
                                ) === true
                                  ? "active"
                                  : "inactive"
                              }`}
                            >
                              {parsePolicyFlag(
                                row.metadata?.mandatory_market_coverage
                              ) === true
                                ? "Coverage Req"
                                : "Coverage Off"}
                            </span>
                          </div>
                        </td>

                        {visibleMetadataKeys.map((key) => (
                          <td key={`${row.code}-${key}`}>
                            {row.metadata && row.metadata[key] !== undefined
                              ? displayValue(row.metadata[key])
                              : "NA"}
                          </td>
                        ))}

                        <td>
                          <div className="action-col">
                            <button
                              className="ghost-btn"
                              onClick={() => toggleExpand(row.code)}
                            >
                              {expanded ? "Hide" : "More"}
                            </button>

                            <button
                              className="secondary-btn small"
                              onClick={() => openMetaModal(row)}
                            >
                              {row.metadata_available
                                ? "Edit Metadata"
                                : "Add Metadata"}
                            </button>

                            <button
                              className={`toggle-btn ${
                                String(row.is_active).toLowerCase() === "active"
                                  ? "danger"
                                  : "success"
                              }`}
                              onClick={() => handleStatusToggle(row)}
                              disabled={updatingCode === row.code}
                            >
                              {String(row.is_active).toLowerCase() === "active"
                                ? "Set Inactive"
                                : "Set Active"}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expanded && (
                        <tr className="expanded-row">
                          <td colSpan={8 + visibleMetadataKeys.length}>
                            <div className="expanded-content">
                              <div className="expanded-grid">
                                <div className="detail-card">
                                  <div className="detail-title">User Data</div>
                                  <div className="detail-body">
                                    <pre>
                                      {JSON.stringify(
                                        row.user_data || {},
                                        null,
                                        2
                                      )}
                                    </pre>
                                  </div>
                                </div>

                                <div className="detail-card">
                                  <div className="detail-title">Actor Data</div>
                                  <div className="detail-body">
                                    <pre>
                                      {JSON.stringify(
                                        row.actor_data || {},
                                        null,
                                        2
                                      )}
                                    </pre>
                                  </div>
                                </div>

                                <div className="detail-card">
                                  <div className="detail-title">Metadata</div>
                                  <div className="detail-body">
                                    <pre>
                                      {JSON.stringify(
                                        row.metadata || {},
                                        null,
                                        2
                                      )}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {metaModalOpen && (
        <div className="ud-modal-overlay" onClick={closeMetaModal}>
          <div className="ud-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ud-modal-header">
              <div>
                <div className="ud-modal-title">
                  {selectedUser?.metadata_available
                    ? "Edit Metadata"
                    : "Add Metadata"}
                </div>
                <div className="ud-modal-subtitle">
                  {selectedUser?.name} • {selectedUser?.code}
                </div>
              </div>

              <button className="close-btn" onClick={closeMetaModal}>
                ×
              </button>
            </div>

            {metaLoading ? (
              <div className="ud-empty">Loading metadata...</div>
            ) : (
              <>
                <div className="ud-modal-body">
                  <div className="modal-grid">
                    <div className="field">
                      <label>Name</label>
                      <input
                        value={metaForm.name}
                        onChange={(e) =>
                          handleMetaInput("name", e.target.value)
                        }
                      />
                    </div>

                    <div className="field">
                      <label>Code</label>
                      <input value={metaForm.code} disabled />
                    </div>

                    <div className="field">
                      <label>System Code</label>
                      <input
                        value={metaForm.system_code}
                        onChange={(e) =>
                          handleMetaInput("system_code", e.target.value)
                        }
                      />
                    </div>

                    <div className="field">
                      <label>Firm Code</label>
                      <select
                        value={metaForm.firm_code}
                        onChange={(e) =>
                          handleMetaInput("firm_code", e.target.value)
                        }
                      >
                        <option value="">Select Firm</option>
                        {firmOptions.map((firm) => (
                          <option key={firm.code} value={firm.code}>
                            {firm.name} ({firm.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <label>Basic Salary</label>
                      <input
                        value={metaForm.basic_salary}
                        onChange={(e) =>
                          handleMetaInput("basic_salary", e.target.value)
                        }
                        placeholder="30000"
                      />
                    </div>

                    <div className="field">
                      <label>Allowed Leaves</label>
                      <input
                        value={metaForm.allowed_leaves}
                        onChange={(e) =>
                          handleMetaInput("allowed_leaves", e.target.value)
                        }
                        placeholder="12"
                      />
                    </div>

                    <div className="field">
                      <label>Leaves Balance</label>
                      <input
                        value={metaForm.leaves_balance}
                        onChange={(e) =>
                          handleMetaInput("leaves_balance", e.target.value)
                        }
                        placeholder="10"
                      />
                    </div>

                    <div className="field">
                      <label>Punch Location</label>
                      <input
                        value={metaForm.punch_location}
                        onChange={(e) =>
                          handleMetaInput("punch_location", e.target.value)
                        }
                        placeholder="open"
                      />
                    </div>

                    <div className="field checkbox-field">
                      <label>Attendance Enabled</label>
                      <div className="switch-row">
                        <input
                          type="checkbox"
                          checked={!!metaForm.attendance}
                          onChange={(e) =>
                            handleMetaInput("attendance", e.target.checked)
                          }
                        />
                        <span>{metaForm.attendance ? "Yes" : "No"}</span>
                      </div>
                    </div>
                    <div className="field checkbox-field">
                      <label>Leaves Enabled</label>
                      <div className="switch-row">
                        <input
                          type="checkbox"
                          checked={!!metaForm.leaves}
                          onChange={(e) =>
                            handleMetaInput("leaves", e.target.checked)
                          }
                        />
                        <span>{metaForm.leaves ? "Yes" : "No"}</span>
                      </div>
                    </div>

                    <div className="field checkbox-field">
                      <label>Use Payroll Policy (PF/ESI)</label>
                      <div className="switch-row">
                        <input
                          type="checkbox"
                          checked={!!metaForm.use_payroll_policy}
                          onChange={(e) =>
                            handleMetaInput("use_payroll_policy", e.target.checked)
                          }
                        />
                        <span>{metaForm.use_payroll_policy ? "Yes" : "No"}</span>
                      </div>
                    </div>

                    <div className="field checkbox-field">
                      <label>Mandatory Market Coverage</label>
                      <div className="switch-row">
                        <input
                          type="checkbox"
                          checked={!!metaForm.mandatory_market_coverage}
                          onChange={(e) =>
                            handleMetaInput(
                              "mandatory_market_coverage",
                              e.target.checked
                            )
                          }
                        />
                        <span>
                          {metaForm.mandatory_market_coverage ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>

                    {metaForm.mandatory_market_coverage && (
                      <div className="field checkbox-field">
                        <label>Allow Attendance From Dealer Point</label>
                        <div className="switch-row">
                          <input
                            type="checkbox"
                            checked={!!metaForm.allow_attendance_from_dealer_point}
                            onChange={(e) =>
                              handleMetaInput(
                                "allow_attendance_from_dealer_point",
                                e.target.checked
                              )
                            }
                          />
                          <span>
                            {metaForm.allow_attendance_from_dealer_point
                              ? "Yes"
                              : "No"}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ud-modal-footer">
                  <button className="secondary-btn" onClick={closeMetaModal}>
                    Cancel
                  </button>
                  <button
                    className="primary-btn"
                    onClick={saveMetadata}
                    disabled={metaSaving}
                  >
                    {metaSaving ? "Saving..." : "Save Metadata"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
